import React, { useEffect, useState } from "react";
import { AUTH_BASE_URL } from "./config";
import { getAuthToken } from "./auth/authUtils";
import "./AdminLoginHistory.css";

const PAGE_SIZE = 25;
const LIMIT_INCREMENT = 200;
const MAX_LIMIT = 2000;

const AdminLoginHistory = () => {
  const [records, setRecords] = useState([]);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(200);
  const [hasNext, setHasNext] = useState(false);
  const [canRetrieveMore, setCanRetrieveMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const controller = new AbortController();
    async function loadHistory() {
      setLoading(true);
      setError("");
      try {
        const params = new URLSearchParams({ page, size: PAGE_SIZE, limit });
        const response = await fetch(`${AUTH_BASE_URL}/auth/admin/login-history?${params}`, {
          headers: { Authorization: `Bearer ${getAuthToken()}` },
          cache: "no-store",
          signal: controller.signal,
        });
        if (!response.ok) throw new Error(`Login history request failed (${response.status})`);
        const data = await response.json();
        setRecords(Array.isArray(data.records) ? data.records : []);
        setHasNext(Boolean(data.hasNext));
        setCanRetrieveMore(Boolean(data.canRetrieveMore));
      } catch (requestError) {
        if (requestError.name !== "AbortError") {
          setError(requestError.message || "Unable to load login history");
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }
    loadHistory();
    return () => controller.abort();
  }, [page, limit]);

  const retrieveMore = () => {
    setLimit((current) => Math.min(current + LIMIT_INCREMENT, MAX_LIMIT));
  };

  return (
    <section className="admin-section login-history">
      <div className="login-history__heading">
        <div>
          <h2>Login History</h2>
          <p>Successful and rejected sign-in attempts, newest first. Browsing is limited to {limit} records.</p>
        </div>
        <button type="button" onClick={() => setPage(0)} disabled={loading || page === 0}>
          Latest
        </button>
      </div>

      {error && <p className="admin-error">{error}</p>}
      {!error && (
        <div className="login-history__table-wrap">
          <table className="admin-table login-history__table">
            <thead>
              <tr>
                <th>Date &amp; time</th>
                <th>Email</th>
                <th>Result</th>
                <th>IP address</th>
                <th>Browser / device</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="5">Loading login history...</td></tr>
              ) : records.length === 0 ? (
                <tr><td colSpan="5">No login attempts found.</td></tr>
              ) : records.map((record) => (
                <tr key={record.id}>
                  <td>{new Date(record.attemptedAt).toLocaleString()}</td>
                  <td>{record.email}</td>
                  <td>
                    <span className={`login-result login-result--${record.successful ? "success" : "rejected"}`}>
                      {record.successful ? "Success" : record.outcome?.replaceAll("_", " ") || "Rejected"}
                    </span>
                  </td>
                  <td>{record.ipAddress || "—"}</td>
                  <td className="login-history__user-agent" title={record.userAgent || ""}>
                    {record.userAgent || "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="login-history__pagination" aria-label="Login history pagination">
        <button type="button" onClick={() => setPage((current) => current - 1)} disabled={loading || page === 0}>
          Previous
        </button>
        <span>Page {page + 1}</span>
        <button type="button" onClick={() => setPage((current) => current + 1)} disabled={loading || !hasNext}>
          Next
        </button>
        {canRetrieveMore && (
          <button className="login-history__more" type="button" onClick={retrieveMore} disabled={loading || limit >= MAX_LIMIT}>
            Retrieve 200 older records
          </button>
        )}
      </div>
    </section>
  );
};

export default AdminLoginHistory;
